import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';

const Scroller = styled.div`
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;

  &:focus-visible {
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;

/**
 * Горизонтальная прокрутка широкой таблицы внутри её контейнера, а не всей
 * страницы. Когда содержимое не помещается, область получает фокус с
 * клавиатуры (Tab, затем стрелки) и имя для скринридера; когда помещается —
 * лишней остановки Tab нет.
 */
export function ScrollRegion({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    const inner = el.firstElementChild;
    if (inner) observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  return (
    <Scroller
      ref={ref}
      className={className}
      tabIndex={overflowing ? 0 : undefined}
      role={overflowing ? 'region' : undefined}
      aria-label={overflowing ? label : undefined}
    >
      {children}
    </Scroller>
  );
}
